import {
  getDefaultCredentials,
  buildMapsUrlFromBase,
  CloudNativeCredentials
} from '../config';
import {
  API_VERSIONS,
  COLUMNS_SUPPORT,
  encodeParameter,
  Format,
  FORMATS,
  GEO_COLUMN_SUPPORT,
  MapInstantiation,
  
  MapType,
  MAP_TYPES,
  QueryParameters,
  REQUEST_TYPES,
  SchemaField,
  TileFormat,
  TILE_FORMATS,
  Filters
} from './maps-api-common';

import {APIErrorContext, CartoAPIError} from './carto-api-error';

import {log} from '@deck.gl/core';

// @ts-ignore
function assert(condition: unknown, message?: string) {
  log.assert(condition, message);
}

const MAX_GET_LENGTH = 8192;
const DEFAULT_CLIENT = 'deck-gl-carto';
const V3_MINOR_VERSION = '3.2';

export type Headers = Record<string, string>;
interface RequestParams {
  method?: string;
  url: string;
  headers?: Headers;
  accessToken?: string;
  body?: any;
  errorContext: APIErrorContext;
}

/**
 * Request against Maps API
 */
async function request({
  method,
  url,
  headers: customHeaders,
  accessToken,
  body,
  errorContext
}: RequestParams): Promise<Response> {
  const headers: Headers = {
    ...customHeaders,
    Accept: 'application/json',
    'Cache-Control': 'max-age=300'
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (method === 'POST') {
    headers['Content-Type'] = 'application/json';
  }

  try {
    /* global fetch */
    return await fetch(url, {
      method,
      headers,
      body
    });
  } catch (error) {
    throw new CartoAPIError(error as Error, errorContext);
  }
}

// @ts-ignore
async function requestJson<T = unknown>({
  method,
  url,
  headers,
  accessToken,
  body,
  errorContext
}: RequestParams): Promise<T> {
  const response = await request({method, url, headers, accessToken, body, errorContext});
  let json;
  try {
    json = await response.json();
  } catch (error) {
    json = {error: ''};
  }

  if (!response.ok) {
    throw new CartoAPIError(json.error, errorContext, response);
  }
  return json as T;
}

async function requestData({
  method,
  url,
  accessToken,
  format,
  body,
  errorContext
}: RequestParams & {
  format: Format;
// @ts-ignore
}): Promise<Response | unknown> {
  if (format === FORMATS.NDJSON) {
    return request({method, url, accessToken, body, errorContext});
  }

  const data = await requestJson<any>({method, url, accessToken, body, errorContext});
  return data.rows ? data.rows : data;
}

type FetchLayerDataParams = {
  type: MapType;
  source: string;
  connection: string;
  credentials: CloudNativeCredentials;
  geoColumn?: string;
  columns?: string[];
  clientId?: string;
  format?: Format;
  formatTiles?: TileFormat;
  headers?: Headers;
  aggregationExp?: string;
  aggregationResLevel?: number;
  queryParameters?: QueryParameters;
  filters?: Filters;
};

type MapsAPIParameters = {
  client: string;
  v: string;
  q?: string;
  name?: string;
  queryParameters?: QueryParameters;
  geo_column?: string;
  columns?: string;
  aggregationExp?: string;
  aggregationResLevel?: number;
  filters?: Filters;
};

/**
 * Build a URL with all required parameters
 */
function getParameters({
  type,
  source,
  geoColumn,
  columns,
  clientId,
  aggregationExp,
  aggregationResLevel,
  queryParameters,
  filters
// @ts-ignore
}: Omit<FetchLayerDataParams, 'connection' | 'credentials'>): MapsAPIParameters {
  const parameters: MapsAPIParameters = {
    client: clientId || DEFAULT_CLIENT,
    v: V3_MINOR_VERSION
  };

  const sourceName = type === MAP_TYPES.QUERY ? 'q' : 'name';
  parameters[sourceName] = source;

  if (queryParameters) {
    parameters.queryParameters = queryParameters;
  }

  if (geoColumn) {
    parameters.geo_column = geoColumn;
  }
  if (columns) {
    parameters.columns = columns.join(',');
  }
  if (aggregationExp) {
    parameters.aggregationExp = aggregationExp;
  } else if (isSpatialIndexGeoColumn(geoColumn)) {
    // Default aggregationExp required for spatial index layers
    parameters.aggregationExp = '1 AS value';
  }
  if (aggregationResLevel) {
    parameters.aggregationResLevel = aggregationResLevel;
  }
  if (filters) {
    parameters.filters = filters;
  }

  return parameters;
}

function isSpatialIndexGeoColumn(geoColumn: string | undefined) {
  const spatialIndex = geoColumn && geoColumn.split(':')[0];
  return spatialIndex === 'h3' || spatialIndex === 'quadbin';
}

async function mapInstantiation({
  type,
  source,
  connection,
  credentials,
  geoColumn,
  columns,
  clientId,
  headers,
  aggregationExp,
  aggregationResLevel,
  queryParameters,
  filters
}: FetchLayerDataParams): Promise<MapInstantiation> {
  const baseUrl = `${credentials.mapsUrl}/${connection}/${type}`;
  const parameters = getParameters({
    type,
    source,
    geoColumn,
    columns,
    clientId,
    aggregationResLevel,
    aggregationExp,
    queryParameters,
    filters
  });

  const encodedParametersEntries = Object.keys(parameters).map(key => [key, parameters[key]]);
  const encodedParameters = encodedParametersEntries.map(([key, value]) => {
    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }
    return encodeParameter(key, value);
  });

  const url = `${baseUrl}?${encodedParameters.join('&')}`;
  const {accessToken} = credentials;

  const errorContext = {requestType: REQUEST_TYPES.INSTANTIATION, connection, type, source};
  if (url.length > MAX_GET_LENGTH && type === MAP_TYPES.QUERY) {
    return await requestJson({
      method: 'POST',
      url: baseUrl,
      headers,
      accessToken,
      body: JSON.stringify(parameters),
      errorContext
    });
  }

  return await requestJson({url, headers, accessToken, errorContext});
}

function getUrlFromMetadata(metadata: MapInstantiation, format: Format): string | null {
  const m = metadata[format];

  if (m && !m.error && m.url) {
    return m.url[0];
  }

  return null;
}

function checkFetchLayerDataParameters({
  type,
  source,
  connection,
  credentials,
  geoColumn,
  columns,
  aggregationExp,
  aggregationResLevel,
  filters
}: FetchLayerDataParams) {
  assert(connection, 'Must define connection');
  assert(type, 'Must define a type');
  assert(source, 'Must define a source');

  assert(credentials.apiVersion === API_VERSIONS.V3, 'Method only available for v3');
  assert(credentials.apiBaseUrl, 'Must define apiBaseUrl');
  assert(credentials.accessToken, 'Must define an accessToken');

  if (columns) {
    assert(
      COLUMNS_SUPPORT.includes(type),
      `The columns parameter is not supported by type ${type}`
    );
  }
  if (geoColumn) {
    assert(
      GEO_COLUMN_SUPPORT.includes(type),
      `The geoColumn parameter is not supported by type ${type}`
    );
  } else {
    assert(!aggregationExp, 'Have aggregationExp, but geoColumn parameter is missing');
    assert(!aggregationResLevel, 'Have aggregationResLevel, but geoColumn parameter is missing');
  }
  if (filters) {
    assert(
      type === MAP_TYPES.TABLE || type === MAP_TYPES.QUERY,
      'The filters parameter is only supported by type table and query'
    );
  }
}

export interface FetchLayerDataResult {
  data: any;
  format?: Format;
  schema: SchemaField[];
}
export async function fetchLayerData({
  type,
  source,
  connection,
  credentials,
  geoColumn,
  columns,
  format,
  formatTiles,
  clientId,
  headers,
  aggregationExp,
  aggregationResLevel,
  queryParameters,
  filters
}: FetchLayerDataParams): Promise<FetchLayerDataResult> {
  // Internally we split data fetching into two parts to allow us to
  // conditionally fetch the actual data, depending on the metadata state
  const {url, accessToken, mapFormat, metadata} = await _fetchDataUrl({
    type,
    source,
    connection,
    credentials,
    geoColumn,
    columns,
    format,
    formatTiles,
    clientId,
    headers,
    aggregationExp,
    aggregationResLevel,
    queryParameters,
    filters
  });

  const errorContext = {requestType: REQUEST_TYPES.DATA, connection, type, source};
  const data = await requestData({url, format: mapFormat, accessToken, errorContext});
  const result: FetchLayerDataResult = {data, format: mapFormat, schema: metadata.schema};
  return result;
}

async function _fetchDataUrl({
  type,
  source,
  connection,
  credentials,
  geoColumn,
  columns,
  format,
  formatTiles,
  clientId,
  headers,
  aggregationExp,
  aggregationResLevel,
  queryParameters,
  filters
}: FetchLayerDataParams) {
  const defaultCredentials = getDefaultCredentials();
  // Only pick up default credentials if they have been defined for
  // correct API version
  const localCreds = {
    ...(defaultCredentials.apiVersion === API_VERSIONS.V3 && defaultCredentials),
    ...credentials
  };
  checkFetchLayerDataParameters({
    type,
    source,
    connection,
    credentials: localCreds,
    geoColumn,
    columns,
    aggregationExp,
    aggregationResLevel,
    filters
  });

  if (!localCreds.mapsUrl) {
    localCreds.mapsUrl = buildMapsUrlFromBase(localCreds.apiBaseUrl);
  }

  const metadata = await mapInstantiation({
    type,
    source,
    connection,
    credentials: localCreds,
    geoColumn,
    columns,
    clientId,
    headers,
    aggregationExp,
    aggregationResLevel,
    queryParameters,
    filters
  });
  let url: string | null = null;
  let mapFormat: Format | undefined;

  if (format) {
    mapFormat = format;
    url = getUrlFromMetadata(metadata, format);
    assert(url, `Format ${format} not available`);
  } else {
    // guess map format
    const prioritizedFormats = [FORMATS.GEOJSON, FORMATS.JSON, FORMATS.NDJSON, FORMATS.TILEJSON];
    for (const f of prioritizedFormats) {
      url = getUrlFromMetadata(metadata, f);
      if (url) {
        mapFormat = f;
        break;
      }
    }
    assert(url && mapFormat, 'Unsupported data formats received from backend.');
  }

  if (format === FORMATS.TILEJSON && formatTiles) {
    const values = Object.keys(TILE_FORMATS).map(key => TILE_FORMATS[key]);

    log.assert(
      values.includes(formatTiles),
      `Invalid value for formatTiles: ${formatTiles}. Use value from TILE_FORMATS`
    );
    url += `&${encodeParameter('formatTiles', formatTiles)}`;
  }

  const {accessToken} = localCreds;
  return {url, accessToken, mapFormat, metadata};
}





// WEBPACK FOOTER //
// ./src/app/common/services/fetch-layer-data/api/maps-v3-client.ts